/**
 * Roster identity resolution — the "smart matching" engine behind the
 * Excel importer.
 *
 * THE PROBLEM IT SOLVES
 * The old importer matched people by their exact full-name string. When
 * HR's file spelled a name even slightly differently from the database
 * — "Sofia Finzi" vs "Sofia Giulia Finzi Longo", "Lóránt Dékány" vs
 * "Lorant Dekany", "Giada Pioggia" vs "Giada Pioggia Todoerto" — the
 * importer saw a brand-new person AND, because the old spelling was now
 * "missing" from the file, marked the real person as a former employee.
 * One human became two database rows: a phantom new hire plus a wrongly
 * retired original.
 *
 * THE FIX — layered identity resolution
 *   1. Email (the stable corporate identity) — exact, normalized match,
 *      guarded by a name-sanity check so a reused/typo'd email can't
 *      silently merge two unrelated people.
 *   2. Exact full name (accent/case-folded) — catches people whose email
 *      drifted but whose name is identical.
 *   3. Fuzzy name — a STRICT token-subset match (same given name + a
 *      shared *surname* token + one name's tokens fully contained in the
 *      other's). Catches "shortened surname" drift without ever merging
 *      two genuinely different people.
 * Anything that matches none of the three is a real new hire; any active
 * database person matched by nothing is a real leaver.
 *
 * WHY WE NEVER RENAME THE PRIMARY KEY
 * The roster's `name` column is the primary key referenced (with ON
 * UPDATE CASCADE) by calendar / notes / meetings / profiles. Renaming it
 * on a live system would cascade an UPDATE across every one of a person's
 * calendar rows — a realtime-broadcast storm to every connected client.
 * We avoid all of that: the *displayed* name is derived from the
 * `first_name` / `last_name` fields (see names.ts), which are NOT the
 * primary key and carry no foreign keys. So we correct a person's
 * spelling by updating those fields — the UI shows the new name instantly,
 * the stable PK keeps all history attached, and nothing cascades.
 *
 * HR-FILE HAZARDS THIS ENGINE GUARDS AGAINST (found in adversarial review)
 *   - The same person listed on two rows (dedup the Excel side first).
 *   - An existing employee's email pasted onto a different person's row
 *     (email match requires weak name agreement, else → conflict).
 *   - A new hire whose email collides with an existing or another new
 *     hire's email (findEmailIssues → blocking validation, never a
 *     silent UNIQUE-constraint abort mid-write).
 *
 * This module is pure (no Preact, no signals, no IO) so the matching
 * contract is locked down by unit tests against the real-world cases.
 */

/** One person as read from the HR Excel "People" sheet. */
export interface ExcelPerson {
  firstName: string;
  lastName: string;
  /** Lower-cased, trimmed. May be "" if the file row had no email. */
  email: string;
  /** Already composed with secondment, e.g. "Business innovation / Lodi". */
  unit: string;
  role: string;
}

/** One person as it currently exists in the database roster. */
export interface DbPerson {
  /** Primary key — stable internal identity, never renamed by the import. */
  name: string;
  email: string;
  unit: string;
  role: string;
  firstName: string;
  lastName: string;
  active: boolean;
}

export type MatchMethod = "email" | "exact-name" | "fuzzy";

/** A single field that the import would change on an existing person. */
export interface FieldChange {
  field: "unit" | "role" | "first_name" | "last_name" | "email" | "reactivate";
  from: string;
  to: string;
}

export interface PlannedUpdate {
  db: DbPerson;
  excel: ExcelPerson;
  method: MatchMethod;
  changes: FieldChange[];
  /** True when first_name or last_name differs — a "name correction". */
  nameChanged: boolean;
  /** True when the person was former in the DB and reappears in the file. */
  reactivated: boolean;
}

/** An email matched an existing person but the names are wholly unrelated
 *  — almost certainly a reused / mistyped email in the HR file. We refuse
 *  to auto-apply it (it would overwrite one person's identity with
 *  another's) and surface it for manual reconciliation. */
export interface PlanConflict {
  excel: ExcelPerson;
  db: DbPerson;
  detail: string;
}

export interface RosterPlan {
  /** Excel rows that matched no existing person — genuine new hires. */
  newHires: ExcelPerson[];
  /** Existing people with at least one field to change. */
  updates: PlannedUpdate[];
  /** Existing people matched with zero changes (count-only in the UI). */
  unchangedCount: number;
  /** Active DB people matched by nothing in the file — genuine leavers. */
  leavers: DbPerson[];
  /** Duplicate Excel rows collapsed away (same email, or same folded name
   *  when email blank). Shown as info; the first occurrence wins. */
  duplicateExcelRows: ExcelPerson[];
  /** Email-matched-but-name-unrelated rows held back from auto-apply. */
  conflicts: PlanConflict[];
}

/** A blocking email-uniqueness problem that must be resolved (in the file)
 *  before the import can run, because roster.email is UNIQUE NOT NULL and
 *  a collision would abort the write mid-batch. */
export interface EmailIssue {
  excel: ExcelPerson;
  email: string;
  /** Human description of what it collides with. */
  collidesWith: string;
}

// ───────────────────────── Normalization ─────────────────────────

/** Accent/case/punctuation fold for robust comparison. */
export function foldText(s: string): string {
  return String(s ?? "")
    .toLowerCase()
    .normalize("NFD")
    // Strip combining accent marks (U+0300–U+036F) to "" FIRST, so an
    // accented letter collapses to its base ("é"→"e") rather than being
    // split by the punctuation pass below ("dékány" → "dekany").
    .replace(/[̀-ͯ]/g, "")
    .replace(/['’`]/g, "") // apostrophes vanish: "D'Onofrio" → "donofrio"
    .replace(/[^a-z0-9\s]/g, " ") // any remaining punctuation → space
    .replace(/\s+/g, " ")
    .trim();
}

export function normEmail(s: string): string {
  return String(s ?? "").trim().toLowerCase();
}

function tokens(s: string): string[] {
  const f = foldText(s);
  return f ? f.split(" ").filter(Boolean) : [];
}

function excelFullName(p: ExcelPerson): string {
  return `${p.firstName} ${p.lastName}`.replace(/\s+/g, " ").trim();
}

/** Folded "first last" key for a DB person. Prefers the split fields,
 *  falling back to the PK name when the split is empty (older rows). */
function dbNameKey(p: DbPerson): string {
  const composed = `${p.firstName} ${p.lastName}`.trim();
  return foldText(composed || p.name);
}

function dbAllTokens(p: DbPerson): string[] {
  const composed = `${p.firstName} ${p.lastName}`.trim();
  return tokens(composed || p.name);
}

/** The DB person's SURNAME tokens — the last_name field when present, else
 *  every token after the first (best-effort for un-split rows). */
function dbLastTokens(p: DbPerson): Set<string> {
  if (p.lastName.trim()) return new Set(tokens(p.lastName));
  const all = dbAllTokens(p);
  return new Set(all.slice(1));
}

/** iCons email convention: first names join with ".", surname collapses
 *  internal spaces. Exported so the importer and the validation share one
 *  derivation. Returns "" when both parts fold to nothing (degenerate). */
export function deriveEmail(firstName: string, lastName: string, domain: string): string {
  const firstSlug = foldText(firstName).split(" ").filter(Boolean).join(".");
  const lastSlug = foldText(lastName).replace(/\s+/g, "");
  const local = [firstSlug, lastSlug].filter(Boolean).join(".");
  return local ? `${local}@${domain}` : "";
}

/** The email a new hire will actually be written with: the file's email
 *  when present, otherwise the derived convention. */
export function effectiveNewHireEmail(p: ExcelPerson, domain: string): string {
  const e = normEmail(p.email);
  return e || deriveEmail(p.firstName, p.lastName, domain);
}

// ───────────────────────── Fuzzy matcher ─────────────────────────

/** Strict confidence test for "same person, different spelling".
 *
 *  Requirements (ALL must hold) — tuned to never merge two genuinely
 *  different people, because a false merge would attribute one person's
 *  attendance history to another:
 *    1. Same leading given-name token.
 *    2. One name's full token set is a subset of the other's (only added
 *       or dropped tokens, never a *changed* token).
 *    3. At least one shared token that is a SURNAME token on the DB side
 *       (uses the DB person's last_name split, so a shared *given* name
 *       — "Cesar Giovanni" vs "Cesar Giovanni-as-surname" — does not
 *       qualify on its own).
 *    4. The smaller name has at least two tokens. */
export function isFuzzyNameMatch(excel: ExcelPerson, db: DbPerson): boolean {
  const eTok = tokens(excelFullName(excel));
  const dTok = dbAllTokens(db);
  if (eTok.length < 2 || dTok.length < 2) return false;

  if (eTok[0] !== dTok[0]) return false; // same given name

  const eSet = new Set(eTok);
  const dSet = new Set(dTok);
  const eSubsetOfD = [...eSet].every((t) => dSet.has(t));
  const dSubsetOfE = [...dSet].every((t) => eSet.has(t));
  if (!eSubsetOfD && !dSubsetOfE) return false;

  // Shared SURNAME token (DB-side surname set), not merely "not the first".
  const dbSurnames = dbLastTokens(db);
  const excelSurnameTokens = tokens(excel.lastName);
  const sharedSurname = excelSurnameTokens.some((t) => dbSurnames.has(t));
  if (!sharedSurname) return false;

  return true;
}

/** Do two names share at least one folded token? Used as the weak
 *  sanity check on email matches. */
function namesShareAnyToken(excel: ExcelPerson, db: DbPerson): boolean {
  const e = new Set(tokens(excelFullName(excel)));
  return dbAllTokens(db).some((t) => e.has(t));
}

// ───────────────────────── Plan builder ──────────────────────────

/** Collapse duplicate Excel rows: same normalized email, or — when email
 *  is blank — same folded full name. First occurrence wins; the rest are
 *  returned separately for the preview. */
function dedupExcel(people: ExcelPerson[]): { unique: ExcelPerson[]; duplicates: ExcelPerson[] } {
  const seenEmail = new Set<string>();
  const seenName = new Set<string>();
  const unique: ExcelPerson[] = [];
  const duplicates: ExcelPerson[] = [];
  for (const p of people) {
    const e = normEmail(p.email);
    const nk = foldText(excelFullName(p));
    if (e) {
      if (seenEmail.has(e)) { duplicates.push(p); continue; }
      seenEmail.add(e);
    } else {
      if (nk && seenName.has(nk)) { duplicates.push(p); continue; }
      if (nk) seenName.add(nk);
    }
    unique.push(p);
  }
  return { unique, duplicates };
}

/** Resolve every Excel row to an existing person or a new hire, find the
 *  leavers, dedup the Excel side, and quarantine email/name conflicts.
 *  `emailDomain` is only used to compute email-correction diffs on
 *  name-matched people. */
export function buildRosterPlan(
  excelPeopleRaw: ExcelPerson[],
  dbPeople: DbPerson[],
  emailDomain = "",
): RosterPlan {
  const { unique: excelPeople, duplicates: duplicateExcelRows } = dedupExcel(excelPeopleRaw);

  const claimed = new Set<string>(); // db.name values already matched
  const matchOf = new Map<ExcelPerson, { db: DbPerson; method: MatchMethod }>();
  const conflicts: PlanConflict[] = [];

  const dbByEmail = new Map<string, DbPerson>();
  const dbByNameKey = new Map<string, DbPerson>();
  for (const d of dbPeople) {
    const e = normEmail(d.email);
    if (e && !dbByEmail.has(e)) dbByEmail.set(e, d);
    const k = dbNameKey(d);
    if (k && !dbByNameKey.has(k)) dbByNameKey.set(k, d);
  }

  const claim = (excel: ExcelPerson, db: DbPerson, method: MatchMethod): void => {
    claimed.add(db.name);
    matchOf.set(excel, { db, method });
  };

  // Layer 1 — email exact, guarded by a name-sanity check.
  for (const x of excelPeople) {
    if (matchOf.has(x) || conflicts.some((c) => c.excel === x)) continue;
    const e = normEmail(x.email);
    if (!e) continue;
    const db = dbByEmail.get(e);
    if (!db || claimed.has(db.name)) continue;
    if (namesShareAnyToken(x, db)) {
      claim(x, db, "email");
    } else {
      // Email lands on a totally unrelated name — likely a reused/typo'd
      // email. Claim the DB person (so it isn't falsely retired) but DON'T
      // rewrite its identity; surface for manual review.
      claimed.add(db.name);
      conflicts.push({
        excel: x,
        db,
        detail: `File email ${e} matches "${db.name}", but the names share no words — possible reused or mistyped email. Not applied automatically.`,
      });
    }
  }

  // Layer 2 — exact folded full name.
  for (const x of excelPeople) {
    if (matchOf.has(x) || conflicts.some((c) => c.excel === x)) continue;
    const db = dbByNameKey.get(foldText(excelFullName(x)));
    if (db && !claimed.has(db.name)) claim(x, db, "exact-name");
  }

  // Layer 3 — strict fuzzy, single-candidate only.
  for (const x of excelPeople) {
    if (matchOf.has(x) || conflicts.some((c) => c.excel === x)) continue;
    const candidates = dbPeople.filter((d) => !claimed.has(d.name) && isFuzzyNameMatch(x, d));
    if (candidates.length === 1) claim(x, candidates[0]!, "fuzzy");
  }

  // Categorize.
  const newHires: ExcelPerson[] = [];
  const updates: PlannedUpdate[] = [];
  let unchangedCount = 0;

  for (const x of excelPeople) {
    if (conflicts.some((c) => c.excel === x)) continue; // quarantined
    const m = matchOf.get(x);
    if (!m) {
      newHires.push(x);
      continue;
    }
    const { db, method } = m;
    const changes: FieldChange[] = [];
    if (db.unit !== x.unit) changes.push({ field: "unit", from: db.unit, to: x.unit });
    if (db.role !== x.role) changes.push({ field: "role", from: db.role, to: x.role });
    if (db.firstName !== x.firstName)
      changes.push({ field: "first_name", from: db.firstName, to: x.firstName });
    if (db.lastName !== x.lastName)
      changes.push({ field: "last_name", from: db.lastName, to: x.lastName });
    // Email correction: only when matched by NAME (an email match means
    // the emails already agree). Surface a non-empty file email that
    // differs from the stored one so a stale/typo'd DB email gets fixed
    // rather than entrenched. Uniqueness is validated separately
    // (findEmailIssues) before any write.
    if (method !== "email") {
      const fileEmail = normEmail(x.email);
      const dbEmail = normEmail(db.email);
      if (fileEmail && fileEmail !== dbEmail) {
        changes.push({ field: "email", from: db.email, to: x.email });
      }
    }
    const reactivated = db.active === false;
    if (reactivated) changes.push({ field: "reactivate", from: "former", to: "active" });

    if (changes.length === 0) {
      unchangedCount++;
      continue;
    }
    const nameChanged = db.firstName !== x.firstName || db.lastName !== x.lastName;
    updates.push({ db, excel: x, method, changes, nameChanged, reactivated });
  }

  const leavers = dbPeople.filter((d) => d.active && !claimed.has(d.name));

  // emailDomain currently only influences new-hire email VALIDATION
  // (findEmailIssues), not the plan shape — referenced to keep the param
  // meaningful and the signature stable.
  void emailDomain;

  return { newHires, updates, unchangedCount, leavers, duplicateExcelRows, conflicts };
}

/** Detect email-uniqueness problems that would make a write fail against
 *  the roster.email UNIQUE NOT NULL constraint. Checks both new-hire
 *  emails and name-match email-corrections against (a) existing roster
 *  emails not belonging to the same person, and (b) each other. Returning
 *  a non-empty list means the import must be blocked until the file is
 *  fixed — far better than a half-applied, aborted batch. */
export function findEmailIssues(
  plan: RosterPlan,
  dbPeople: DbPerson[],
  emailDomain: string,
): EmailIssue[] {
  const issues: EmailIssue[] = [];
  // Existing roster emails → owner name.
  const ownerByEmail = new Map<string, string>();
  for (const d of dbPeople) {
    const e = normEmail(d.email);
    if (e) ownerByEmail.set(e, d.name);
  }
  // Emails this import will write, with their source, to catch in-batch
  // collisions too.
  const writers = new Map<string, ExcelPerson>();

  const consider = (x: ExcelPerson, email: string, selfDbName: string | null): void => {
    const e = normEmail(email);
    if (!e) {
      issues.push({ excel: x, email: "(empty)", collidesWith: "an empty/underivable address" });
      return;
    }
    const owner = ownerByEmail.get(e);
    if (owner && owner !== selfDbName) {
      issues.push({ excel: x, email: e, collidesWith: `existing roster row "${owner}"` });
      return;
    }
    const prior = writers.get(e);
    if (prior && prior !== x) {
      issues.push({ excel: x, email: e, collidesWith: `another imported row (${prior.firstName} ${prior.lastName})` });
      return;
    }
    writers.set(e, x);
  };

  for (const h of plan.newHires) {
    consider(h, effectiveNewHireEmail(h, emailDomain), null);
  }
  for (const u of plan.updates) {
    const emailChange = u.changes.find((c) => c.field === "email");
    if (emailChange) consider(u.excel, emailChange.to, u.db.name);
  }
  return issues;
}
